import React, { useEffect, useRef, useState } from "react";
import { Button, Card, Result, Spin, Alert, Typography } from "antd";
import { LoadingOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../../../utils/axiosInstance";
import { getTenantId } from "../../../utils/auth";
import { JobType } from "../../../constants/JobTypes";
import { StepConfigurationData } from "./StepConfiguration";

const { Text } = Typography;

interface StepProcessingProps {
  jobConfigId: string;
  stepConfigurationData: StepConfigurationData;
  onSuccess: () => void;
}

// Interstitial between Step 2 (Configuration) and Step 3 (Power Analysis) -
// mirrors causal-loop/components/StepProcessing.tsx exactly, but creates a
// CausalLoopRct job (Bid Optimizer + the RCT 50/50 design engine, run
// concurrently server-side by process_causal_loop_rct_job in
// sqs_listener_service.py) instead of Bid Optimizer + GeoLift. The rct_
// experiment_data.csv input was already uploaded by Step 2's own effect (see
// StepConfiguration.tsx), so this step only creates the JobConfig + JobRun
// and polls it until done.
const POLL_INTERVAL_MS = 15000;
const MAX_POLL_ATTEMPTS = 80; // ~20 minutes

const StepProcessing: React.FC<StepProcessingProps> = ({
  jobConfigId,
  stepConfigurationData,
  onSuccess,
}) => {
  const navigate = useNavigate();
  const [phase, setPhase] = useState<"creating" | "polling" | "done" | "failed">("creating");
  const [error, setError] = useState<string | null>(null);
  const submittedRef = useRef(false);
  const pollAttemptsRef = useRef(0);

  useEffect(() => {
    if (submittedRef.current) return;
    submittedRef.current = true;
    submitJob();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submitJob = async () => {
    setPhase("creating");
    setError(null);
    try {
      const cfg = stepConfigurationData;
      const payload = {
        jobConfigId,
        name: cfg.jobName,
        jobType: JobType.CausalLoopRct,
        tenantId: getTenantId(),
        dataSourceConfig: { dataSourceType: "FILE_CSV" },
        dataTransformationConfig: {},
        dataMappingConfig: {
          mappings: {
            bidMultiplier: {
              columnMapping: {
                age_varialbes: cfg.age ? [cfg.age] : undefined,
                device_platform_varialbes: cfg.devicePlatform ? [cfg.devicePlatform] : undefined,
                gender_varialbes: cfg.gender ? [cfg.gender] : undefined,
                locale_varialbes: cfg.locale ? [cfg.locale] : undefined,
                region_varialbes: [cfg.region],
                user_os_varialbes: cfg.userOs ? [cfg.userOs] : undefined,
                target_variables: [cfg.targetVariable],
              },
            },
          },
        },
        modelVersion: "V1",
        modelParameterConfig: {
          parameters: {
            campaignObjective: cfg.campaignObjective,
            geoLocationType: cfg.geoLocationType,
            dateFormat: "%Y-%m-%d",
            // Not read by process_rct_experiment_job (it downloads the
            // already-built rct_experiment_data.csv, not this raw column
            // name) - stored purely so a "View" of this job can reconstruct
            // Step 2's read-only form without the original file in this
            // browser session (see CreateCausalLoopRct.tsx's view-mode fetch).
            dateColumn: cfg.dateColumn,
            isSales: cfg.isSales,
            // Always 50/50 - this feature is specifically the RCT 50/50
            // variant of Causal Loop, so the split isn't a user choice (see
            // rct-experiment/components/mockData.ts's SPLIT_PERCENTAGES for
            // the "5050" -> 0.5 convention the design engine expects).
            durations: cfg.durations,
            splits: { "5050": 0.5 },
            totalWeeklySpend: cfg.totalWeeklySpend,
            weeklyInvestmentCap: cfg.weeklyCap,
          },
        },
        modelOutputConfig: {},
      };

      await axiosInstance.post("/api/jobConfig", payload);
      const runResponse = await axiosInstance.post("/api/jobRun", { jobConfigId });
      const jobRunId = (runResponse.data?.jobRun as any)?.jobRunId;

      setPhase("polling");
      pollStatus(jobRunId);
    } catch (err: any) {
      console.error("Error submitting Causal Loop (RCT) job:", err);
      setError(
        err?.response?.data?.detail ||
          "Something went wrong while creating the job. Please try again."
      );
      setPhase("failed");
    }
  };

  const pollStatus = (jobRunId: string | undefined) => {
    if (!jobRunId) {
      return;
    }
    const check = async () => {
      pollAttemptsRef.current += 1;
      try {
        const response = await axiosInstance.get(`/api/jobRun/${jobRunId}`);
        const status = response.data?.jobRun?.jobStatus;
        if (status === "SUCCESSFUL") {
          setPhase("done");
          onSuccess();
          return;
        }
        if (status === "FAILED") {
          setError(
            "Both engines failed to complete. Please check your data and try again, or view the job from the list for details."
          );
          setPhase("failed");
          return;
        }
      } catch (e) {
        // A transient fetch error shouldn't stop polling - keep trying until
        // MAX_POLL_ATTEMPTS.
      }
      if (pollAttemptsRef.current < MAX_POLL_ATTEMPTS) {
        setTimeout(check, POLL_INTERVAL_MS);
      }
    };
    setTimeout(check, POLL_INTERVAL_MS);
  };

  const goToDashboard = () => navigate("/causal-loop-rct/list");

  if (phase === "failed") {
    return (
      <Card className="shadow-sm">
        <Result
          status="error"
          title="Job Submission Failed"
          subTitle={error}
          icon={<ExclamationCircleOutlined className="text-red-500 text-6xl" />}
          extra={[
            <Button type="primary" key="retry" onClick={() => { submittedRef.current = false; submitJob(); }}>
              Try Again
            </Button>,
            <Button key="dashboard" onClick={goToDashboard}>
              Go to Dashboard
            </Button>,
          ]}
        />
      </Card>
    );
  }

  const phaseLabel =
    phase === "creating" ? "Creating job..." : "Running Bid Optimizer + RCT 50/50 design...";

  return (
    <Card className="shadow-sm text-center py-12">
      <Spin indicator={<LoadingOutlined style={{ fontSize: 40 }} spin />} />
      <h3 className="text-lg font-semibold text-gray-900 mt-6">
        {phaseLabel}
      </h3>
      <Text className="text-sm text-gray-500 block max-w-md mx-auto mb-2">
        Bid Optimizer and the RCT 50/50 design are running in parallel against your uploaded
        data. The RCT design's market pairing and trial search can take several minutes - feel
        free to head to the dashboard and come back once it's done; this study's status will
        show as "Running" until then.
      </Text>
      {phase === "polling" && (
        <Alert
          className="mt-4 max-w-md mx-auto text-left"
          type="info"
          showIcon
          message="You can safely leave this page"
          description='This study will keep processing in the background. Come back to "Causal Loop > With RCT" from the sidebar and click "View" once it shows as completed.'
        />
      )}
      <div className="mt-8">
        <Button type="primary" size="large" onClick={goToDashboard}>
          Go to Dashboard
        </Button>
      </div>
    </Card>
  );
};

export default StepProcessing;
